import React, { useEffect, useRef, useState } from 'react';
import { Filter as FilterIcon } from 'lucide-react';
import { getFailureFacets } from 'features/agent-analytics/components/failures/failuresApi';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import { FilterChip, FilterPanel, useDismiss } from 'features/issues/components/IssuesFilter';

const FACETS = [
  { id: 'agents', label: 'Agent' },
  { id: 'tools', label: 'Tool' },
  { id: 'categories', label: 'Category' },
];
const PAGE = 15;

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/**
 * One Filter button for agent, tool and category, using the same popup as the
 * Issues page. Nothing loads until it is opened; then each facet shows its
 * first handful and the search box asks the server for matches. `value` is
 * { agents, tools, categories }, each null (no filter) or the chosen values.
 * Categories also carry their label so a chip reads "Timeout", not "timeout".
 */
export default function FailureFilter({ teamId, period, value, labels, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const q = useDebounced(query, 200);
  const [data, setData] = useState({ agents: [], tools: [], categories: [], agents_total: 0, tools_total: 0 });
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const periodKey = JSON.stringify(period);
  useDismiss(open, setOpen, wrapRef);

  useEffect(() => {
    if (!open || !teamId) return undefined;
    let live = true;
    setLoading(true);
    getFailureFacets(teamId, { period: JSON.parse(periodKey), q: q.trim(), limit: PAGE })
      .then((f) => { if (live) setData(f); })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, teamId, periodKey, q]);

  const chosen = (id) => value[id] || [];
  const nameOf = (id, key) => (id === 'categories' ? (labels?.[key] || key) : key);
  const count = FACETS.reduce((n, f) => n + chosen(f.id).length, 0);

  const sections = FACETS.map((f) => {
    const options = (data[f.id] || []).map((o) => (f.id === 'categories' ? { value: o.key, label: o.label } : { value: o, label: o }));
    const total = f.id === 'agents' ? data.agents_total : f.id === 'tools' ? data.tools_total : options.length;
    return {
      id: f.id,
      label: f.label,
      options,
      selected: chosen(f.id),
      emptyIsAny: true,
      onQuery: setQuery,
      loading,
      footer: total > options.length ? `${options.length} of ${total.toLocaleString()}. Search for the rest.` : null,
      onChange: (next) => {
        const added = f.id === 'categories' ? Object.fromEntries(options.filter((o) => next.includes(o.value)).map((o) => [o.value, o.label])) : {};
        onChange({ ...value, [f.id]: next.length ? next : null }, f.id === 'categories' ? { ...labels, ...added } : labels);
      },
    };
  });

  const clear = (id) => onChange({ ...value, [id]: null }, labels);
  const chipText = (id) => {
    const n = chosen(id).map((k) => nameOf(id, k));
    return n.length <= 2 ? n.join(', ') : `${n.length} selected`;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div ref={wrapRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={activityToolbarButtonClass}
        >
          <FilterIcon size={13} aria-hidden />
          Filter
          {count > 0 ? <span className="text-[11px] tabular-nums text-[color:var(--text-muted)]">{count}</span> : null}
        </button>
        {open ? (
          <FilterPanel sections={sections} label="Filter failures" onClose={() => setOpen(false)}
            onSection={() => setQuery('')} className="absolute left-0 top-full z-50 mt-1" />
        ) : null}
      </div>
      {FACETS.filter((f) => chosen(f.id).length > 0).map((f) => (
        <FilterChip key={f.id} label={f.label} onRemove={() => clear(f.id)}>{chipText(f.id)}</FilterChip>
      ))}
    </div>
  );
}
